import React, { useEffect, useState } from "react"
import { useDispatch } from "react-redux"
import { useTranslation } from "react-i18next"
import { useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { styled } from "@mui/material/styles"
import Stack from "@mui/material/Stack"
import { updateAvatarSchema } from "validations/userProfile"
import { FULFILLED } from "constants/index"
import { AVATAR_UPDATED } from "constants/events"
import { useUpdateAvatarMutation } from "appRedux/slices/userApiSlice"
import { updateShowEnlargedAvatarModal } from "appRedux/slices/globalSlice"
import EditIcon from "components/atoms/_icons/EditIcon"
import TrashIcon from "components/atoms/_icons/TrashIcon"
import WarningRoundedIcon from "components/atoms/_icons/WarningRoundedIcon"
import IconWrapper from "components/atoms/IconWrapper"
import Dialog from "components/atoms/Dialog"
import ImageInputWithCropperModal from "components/molecules/ImageInputWithCropperModal"

type ProfileImageSectionForOwnerProps = {
  loading: boolean;
  onChange: (value: File | null) => void;
}

export default function UpdateAvatarActionButtons(props: ProfileImageSectionForOwnerProps) {
  const { loading, onChange } = props

  const [showDeleteAvatarConfirmationDialog, setShowDeleteAvatarConfirmationDialog] = useState<boolean>(false)

  const [triggerUpdateAvatar, updateAvatarResult] = useUpdateAvatarMutation()

  const { t } = useTranslation()
  const dispatch = useDispatch()

  const {
    register,
    setValue
  } = useForm<UpdateAvatarRequestPayload>({
    resolver: zodResolver(updateAvatarSchema),
    shouldFocusError: false,
    reValidateMode: "onBlur",
    defaultValues: { avatar: new File([], "") }
  })

  useEffect(() => {
    if (updateAvatarResult.status === FULFILLED) {
      document.dispatchEvent(new CustomEvent(AVATAR_UPDATED))
      dispatch(updateShowEnlargedAvatarModal(false))
    }
  }, [updateAvatarResult])

  function handleDeleteAvatarButtonClicked() {
    setShowDeleteAvatarConfirmationDialog(true)
  }

  function deleteAvatar() {
    onChange(null)
  }

  function handleCloseDeleteAvatarConfirmationDialog() {
    setShowDeleteAvatarConfirmationDialog(false)
  }

  async function setItemImage(value: File) {
    setValue("avatar", value, { shouldDirty: true })
    await handleUpdateAvatar(value || null)
  }

  async function handleUpdateAvatar(value: File | null) {
    try {
      const formData = new FormData()
      const parsedValue = await updateAvatarSchema.parseAsync({ avatar: value })
      // @ts-ignore
      formData.append("avatar", parsedValue.avatar)
      triggerUpdateAvatar(formData)
    } catch (err) {
      console.error(err)
    }
  }

  return (
    <>
      <BottomActionsContainer>
        <div id="edit">
          <ImageInputWithCropperModal
            id="avatar"
            register={register}
            setValue={setValue}
            containerId="avatar-wrapper"
            isSubmitting={updateAvatarResult.isLoading}
            isSingleForm={true}
            onChange={setItemImage}
          />
          <IconWrapper>
            <EditIcon/>
          </IconWrapper>
        </div>
        <IconWrapper id="delete" onClick={handleDeleteAvatarButtonClicked}>
          <TrashIcon/>
        </IconWrapper>
      </BottomActionsContainer>

      <Dialog
        message={t("user.deleteAvatarConfirmationQuestion")}
        icon={<WarningRoundedIcon/>}
        buttons={[
          {
            label: t("general.yes"),
            mode: "white",
            loading: loading,
            onClick: deleteAvatar
          },
          {
            label: t("general.no"),
            mode: "primary",
            loading: false,
            disabled: false,
            onClick: handleCloseDeleteAvatarConfirmationDialog
          }
        ]}
        open={showDeleteAvatarConfirmationDialog}
        disableBackdropClose={true}
        onClose={handleCloseDeleteAvatarConfirmationDialog}
      />
    </>
  )
}

const BottomActionsContainer = styled(Stack)(({ theme }) => ({
  width: "100%",
  flexDirection: "row",
  justifyContent: "space-between",
  alignItems: "center",
  padding: "24px 16px",
  position: "absolute",
  bottom: 0,
  outline: "none",

  "& div": {
    borderRadius: "50%",
    backgroundColor: theme.palette.gray["15"]
  },

  "& div#edit": {
    position: "relative",

    "& label": {
      height: "44px",
      position: "absolute",
      top: 0,
      left: 0
    },

    "& svg": {
      stroke: "#FFFFFF"
    }
  },

  "& input": {
    display: "none"
  }
}))
